import React, { useEffect, useMemo, useRef, useState } from "react";
import { Send, Sparkles, RotateCcw } from "lucide-react";
import { API_BASE, scopeParams } from "../lib/api";
import { useSession } from "../context/SessionContext";

const SESSION_STORE_KEY = "angadya.chat.session.v1";

const makeSid = () => {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch { /* ignore */ }
  return "s-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
};

/**
 * AskPanel — chat surface. Renders a message list, canned suggestions on empty
 * state, and an input. Streams tokens from POST /api/chat/message (SSE).
 *
 * `variant="drawer"` is used inside the floating slide-in, `variant="full"`
 * inside the dedicated /app/ask page.
 */
export const AskPanel = ({ variant = "drawer", onClose }) => {
  const { session, focusBranchId } = useSession();
  const scope = useMemo(() => scopeParams(session, focusBranchId), [session, focusBranchId]);
  const scopeKey = JSON.stringify(scope);

  const [sessionId] = useState(() => {
    try {
      const cached = sessionStorage.getItem(SESSION_STORE_KEY);
      if (cached) return cached;
    } catch { /* ignore */ }
    const fresh = makeSid();
    try { sessionStorage.setItem(SESSION_STORE_KEY, fresh); } catch { /* ignore */ }
    return fresh;
  });

  const [messages, setMessages] = useState([]);        // { role: 'user'|'assistant', text }
  const [suggestions, setSuggestions] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState("");
  const abortRef = useRef(null);
  const scrollRef = useRef(null);

  // Load suggestions whenever scope changes
  useEffect(() => {
    let cancelled = false;
    const qs = new URLSearchParams(scope).toString();
    fetch(`${API_BASE}/chat/suggestions?${qs}`)
      .then((r) => r.json())
      .then((d) => { if (!cancelled) setSuggestions(d.suggestions || []); })
      .catch(() => { if (!cancelled) setSuggestions([]); });
    return () => { cancelled = true; };
  }, [scopeKey]);

  // Autoscroll on new tokens
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, streaming]);

  const send = async (text) => {
    const trimmed = (text || "").trim();
    if (!trimmed || streaming) return;
    setError("");
    setInput("");
    setMessages((m) => [...m, { role: "user", text: trimmed }, { role: "assistant", text: "" }]);
    setStreaming(true);

    try {
      const controller = new AbortController();
      abortRef.current = controller;
      const res = await fetch(`${API_BASE}/chat/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId,
          role: session.role,
          branch_id: scope.branch_id || null,
          region: scope.region || null,
          message: trimmed,
        }),
        signal: controller.signal,
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let done = false;
      while (!done) {
        const { value, done: rd } = await reader.read();
        done = rd;
        if (value) buffer += decoder.decode(value, { stream: !rd });

        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const frame = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const line = frame.startsWith("data: ") ? frame.slice(6) : frame;
          if (!line) continue;
          if (line === "[DONE]") { done = true; break; }
          if (line.startsWith("[ERROR]")) {
            setError(line.replace("[ERROR]", "").trim() || "Chat error");
            done = true; break;
          }
          // De-escape our on-wire newline escape
          const chunk = line.replace(/\\n/g, "\n");
          setMessages((m) => {
            const copy = [...m];
            const last = copy[copy.length - 1];
            if (last && last.role === "assistant") {
              copy[copy.length - 1] = { ...last, text: last.text + chunk };
            }
            return copy;
          });
        }
      }
    } catch (e) {
      if (e.name !== "AbortError") setError(String(e.message || e));
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const resetChat = async () => {
    try {
      abortRef.current?.abort();
      await fetch(`${API_BASE}/chat/reset?session_id=${encodeURIComponent(sessionId)}`, { method: "POST" });
    } catch { /* ignore */ }
    setMessages([]);
    setError("");
  };

  const isFull = variant === "full";
  const shellClass = isFull
    ? "flex flex-col h-full ang-panel rounded-2xl overflow-hidden"
    : "flex flex-col h-full bg-white shadow-2xl";

  return (
    <div className={shellClass} data-testid="ask-panel" data-variant={variant}>
      {/* Header */}
      <div
        className="flex items-center gap-3 px-4 py-3 border-b border-ang"
        style={{ background: "linear-gradient(135deg, #14142B 0%, #1E1E45 100%)", color: "#FFFFFF" }}
      >
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center"
          style={{ background: "linear-gradient(135deg, #00B4D8 0%, #7C6FF0 100%)", boxShadow: "0 6px 14px rgba(0,180,216,0.35)" }}
        >
          <Sparkles size={16} strokeWidth={2.4} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.55)" }}>
            Angadya · Ask
          </div>
          <div className="text-sm font-bold leading-tight" style={{ letterSpacing: "-0.01em" }}>
            Talk to your data
          </div>
        </div>
        <button
          type="button"
          onClick={resetChat}
          data-testid="btn-ask-reset"
          title="Start a new conversation"
          className="p-1.5 rounded-lg transition-colors text-white/70 hover:text-white hover:bg-white/[0.08]"
        >
          <RotateCcw size={15} strokeWidth={2.2} />
        </button>
        {!isFull && onClose && (
          <button
            type="button"
            onClick={onClose}
            data-testid="btn-ask-close"
            aria-label="Close chat"
            className="p-1.5 rounded-lg transition-colors text-white/70 hover:text-white hover:bg-white/[0.08]"
          >
            <span className="text-lg leading-none">×</span>
          </button>
        )}
      </div>

      {/* Message list */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" style={{ background: "#F5F6FA" }}>
        {messages.length === 0 && (
          <div className="space-y-3">
            <div className="rounded-2xl p-4" style={{ background: "#FFFFFF", boxShadow: "0 2px 6px rgba(20,20,43,0.05)" }}>
              <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: "#8E90A8" }}>
                Grounded in today&apos;s data
              </div>
              <p className="text-sm mt-1 leading-relaxed" style={{ color: "#14142B" }}>
                Ask an operational question about your branches, revenue, wastage or staffing.
                I read the current dashboard scope automatically.
              </p>
            </div>
            <div className="text-[10px] font-bold uppercase tracking-widest px-1" style={{ color: "#8E90A8" }}>
              Try one of these
            </div>
            <div className="grid grid-cols-1 gap-2">
              {suggestions.map((s) => (
                <button
                  key={s}
                  type="button"
                  data-testid="ask-suggestion"
                  onClick={() => send(s)}
                  className="text-left rounded-xl px-3 py-2.5 text-sm font-medium transition-colors"
                  style={{
                    background: "#FFFFFF",
                    color: "#14142B",
                    boxShadow: "0 2px 6px rgba(20,20,43,0.05)",
                    border: "1px solid rgba(0,180,216,0.15)",
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div
            key={i}
            data-testid={m.role === "user" ? "ask-msg-user" : "ask-msg-assistant"}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className="rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap max-w-[85%]"
              style={
                m.role === "user"
                  ? { background: "linear-gradient(135deg, #00B4D8 0%, #7C6FF0 100%)", color: "#FFFFFF", borderTopRightRadius: 4 }
                  : { background: "#FFFFFF", color: "#14142B", boxShadow: "0 2px 6px rgba(20,20,43,0.05)", borderTopLeftRadius: 4 }
              }
            >
              {m.text || (m.role === "assistant" && streaming ? <TypingDots /> : null)}
            </div>
          </div>
        ))}

        {error && (
          <div className="rounded-xl px-3 py-2 text-xs" style={{ background: "#FFF0EE", color: "#B7402A" }}>
            {error}
          </div>
        )}
      </div>

      {/* Composer */}
      <form
        onSubmit={(e) => { e.preventDefault(); send(input); }}
        className="border-t border-ang p-3 flex items-center gap-2"
        style={{ background: "#FFFFFF" }}
      >
        <input
          data-testid="ask-input"
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about your branches, sales, wastage…"
          className="flex-1 rounded-xl border border-ang px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-teal-300"
          style={{ background: "#F5F6FA", color: "#14142B" }}
          disabled={streaming}
        />
        <button
          type="submit"
          data-testid="btn-ask-send"
          disabled={streaming || !input.trim()}
          className="w-10 h-10 rounded-xl flex items-center justify-center transition-transform hover:scale-[1.03] disabled:opacity-40 disabled:hover:scale-100"
          style={{
            background: "linear-gradient(135deg, #00B4D8 0%, #7C6FF0 100%)",
            color: "#FFFFFF",
            boxShadow: "0 6px 14px rgba(0,180,216,0.35)",
          }}
          aria-label="Send"
        >
          <Send size={15} strokeWidth={2.4} />
        </button>
      </form>
    </div>
  );
};

const TypingDots = () => (
  <span className="inline-flex items-center gap-1">
    <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#7C6FF0", animationDelay: "0ms" }} />
    <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#7C6FF0", animationDelay: "150ms" }} />
    <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#7C6FF0", animationDelay: "300ms" }} />
  </span>
);

export default AskPanel;
